import React, {
    JSX,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import useMeasure from "react-use-measure";
import useInterval from "use-interval";
import { useIsSmallScreen } from "../../App";
import { capitalizeGabcFirstWord } from "../../pages/pray/ordinariate-liturgy";
import { DOT_COLOR, RUBRIC_COLOR, TEXT_SIZE } from "../../tokens";
import { useSettings } from "../../utils/settings";
import { STAVE_SPACING } from "../Chant";
import { LIBER_TONES } from "../tones";
import { getLayoutNeumes } from "./getLayoutNeumes";
import { getNeumesFromGabc } from "./getNeumesFromGabc";
import {
    NOTES,
    SHARP_MAP,
    getTransposeByFromToneObject,
    transposeNote,
} from "./note-utils";

/*

TODO

- Playing note highlight
- Pitch highlight of notes
- Mark tone above drop cap
- LORD
- Check and fix all of the hymns
- Render chants after fonts have loaded
- Fix Lord have mercy
- Add more space before neumes at the beginning of a line
- Dashes and dot at the end of the same line

*/

export type Neume = {
    text: string;
    isBold: boolean;
    isSmallCaps?: boolean;
    elements: NeumeElement[];
    flatPitch?: number;
    hasRedDotBefore?: boolean;
    showFlat?: boolean;
};
export type NeumeElement = {
    type:
        | "punctum"
        | "podatus"
        | "clivis"
        | "scandicus"
        | "torculus"
        | "porrectus"
        | "pes subpunctis"
        | "climacus"
        | "quarter bar"
        | "half bar"
        | "full bar";
    notes: NeumeNote[];
    suppressSpace?: boolean;
};
export type NeumeNote = {
    pitch: number;
    isInclinatum?: boolean;
    hasMora?: boolean;
    hasVerticalEpisemaBelow?: boolean;
    hasHorizontalEpisema?: boolean;
    isNoteFlat?: boolean;
    isQuilisma?: boolean;
    isVirga?: boolean;
    isLiquescent?: boolean;
    addSpace?: boolean;
};

export type LayoutNeume = {
    neume: Neume;
    svgNeume: JSX.Element[];
    textExtensionLeft: number;
    textExtensionRight: number;
    neumeExtensionLeft: number;
    neumeExtensionRight: number;
    redDotOffset: number | undefined;
    maxNote: number;
    minNote: number;
};

const WINDOW: any = window;

if (!WINDOW.measureCanvas) {
    WINDOW.measureCanvas = document.createElement("canvas");
}

const canvasContext = WINDOW.measureCanvas.getContext("2d");

const DROP_CAP_FONT_SIZE = 64;
const BODY_FONT = "EB Garamond";
const STAVE_LINE_SPACING = 11;
const TEXT_OFFSET = 55;
const DROP_CAP_OFFSET = 55;

const DOCUMENT: any = document;

export const getPitchY = (pitch: number) => {
    return STAVE_SPACING * 3.5 - (STAVE_LINE_SPACING / 2) * pitch;
};

export const EBChant: React.FC<{
    gabc: string;
    dropCap: boolean;
    tone: string | undefined;
    onClickNeume?: (neumeIndex: number) => void;
    highlightedNote?: [number, number];
    onReportMistake?: (verseIndex: number) => void;
}> = (props) => {
    const {
        gabc: unfilteredGabc,
        dropCap,
        tone,
        onClickNeume,
        highlightedNote,
    } = props;

    const [mistakeNeume, setMistakeNeume] = useState(-1);
    const [mistakeClicks, setMistakeClicks] = useState(0);

    const filteredGabc = unfilteredGabc
        .replace(/\<sc\>/g, "")
        .replace(/\<\/sc\>/g, "")
        .replace(/%/g, "");

    const gabc = dropCap ? capitalizeGabcFirstWord(filteredGabc) : filteredGabc;

    const isSmallScreen = useIsSmallScreen();

    const bodyFontSize = isSmallScreen ? TEXT_SIZE * 0.9 : TEXT_SIZE;

    const measureText = useCallback(
        (text: string, style: "normal" | "normal bold" | "drop cap") => {
            if (style === "normal") {
                canvasContext.font = `${bodyFontSize}px ${BODY_FONT}`;
            } else if (style === "drop cap") {
                canvasContext.font = `700 ${DROP_CAP_FONT_SIZE}px ${BODY_FONT}`;
            } else if (style === "normal bold") {
                canvasContext.font = `700 ${bodyFontSize}px ${BODY_FONT}`;
            }

            let measurements = canvasContext.measureText(
                text.replace(/-/g, "n")
            );

            return measurements.width;
        },
        []
    );

    const toneLabel =
        LIBER_TONES[tone ?? ""]?.liber ??
        (tone ?? "")
            ?.replace("1", "I")
            .replace("2", "II")
            .replace("3", "III")
            .replace("4", "IV")
            .replace("5", "V")
            .replace("6", "VI")
            .replace("7", "VII")
            .replace("8", "VIII")
            .replace("n", "");

    // const gabc = "(c4) CHris(ffg)tus(f.) *(,) fac(fg)tus(f) est(f') pro(f) no(ghF'ED.g_e/fg)bis(f.) (;) o(f)bé(hf/gh!jj/kjk)di(jij)ens(h_g) (,) us(h!jjh)que(f.) ad(ixfiH'Gh) mor(fgf___)tem,(f.) (:) mor(ixf!hhi)tem(g) au(ixgjj//)tem(j.h!iw!jvIG'hw!ivHG) cru(fv.ff)cis.(f_c) (,) (fg!hvGF.e!g'h//fhg/hgg.f.) V/.(::)  Prop(d)ter(d) quod(d) et(fe~) De(eh)us(h.) (,) e(h)xal(h)tá(h)vit(h) il(h)lum,(ihhe.//hih___/ihhe.) (,) (hi!kv//lvKI'jvHF.) (,) (h_g/ijh'___ jvIH'jvIH'//hvGF.) (:) et(f) de(f!h'i)dit(i) il(i)li(ih/jki/hhf.) (,) no(hf/hhh)men,(h.f!gwh!iv.hi/jhh/iih.) (;) quod(d) est(d) su(d)per(d) o(dfE'D)mne(ef) *() no(fhG'Fhhh)men.(hhf.) (,) (gxg_fgvED.fgED.fe/hv.hhhff//dfe/feed.) (::) X(gh~/hg~/dh~/hd~) Y(ghgi) Z(hghf) A(ghi) B(hgj) C(igh) D(jgh)";

    const clef = gabc.slice(1, 3);

    const containerRef = useRef<HTMLDivElement>(null);
    const [measureRef, measure] = useMeasure();

    const { neumes, dropCapText } = useMemo(() => {
        return getNeumesFromGabc(gabc, dropCap);
    }, [gabc, dropCap]);

    const { settings } = useSettings();

    1;

    const [tuningPitch, setTuningPitch] = useState<undefined | number>(
        undefined
    );

    const isOnScreen = useOnScreen(containerRef);
    const [hasEverBeenOnScreen, setHasEverBeenOnScreen] = useState(false);
    useEffect(() => {
        if (isOnScreen && !hasEverBeenOnScreen) {
            setHasEverBeenOnScreen(true);
        }
    }, [isOnScreen, hasEverBeenOnScreen]);

    const getTuningNote = useCallback(() => {
        if (settings.tuning === "enabled" && tone && isOnScreen) {
            const transposeByKey = tone[0];

            const clef = gabc.slice(1, 3);

            const tuningEvent = WINDOW.tuningEvent;

            if (tuningEvent && tuningEvent.note) {
                const transposeBy =
                    settings.transpositions["1"] +
                    getTransposeByFromToneObject(LIBER_TONES[tone ?? ""]);

                const clefOffset = clef === "c2" ? 3 : clef === "c4" ? 7 : 5;

                const offset =
                    clef === "c3" || clef === "c4" || clef === "c2"
                        ? 9 - clefOffset
                        : 5 - clefOffset;

                const detectedNote = SHARP_MAP[tuningEvent.note];

                const transposedNote = transposeNote(
                    `${detectedNote}${tuningEvent.octave}`,
                    -transposeBy
                );

                const isFlat = transposedNote[1] === "b";

                const transposedNoteWithoutFlat = transposedNote.replace(
                    "b",
                    ""
                );

                const noteValue =
                    NOTES.findIndex((n) => n === transposedNoteWithoutFlat) -
                    offset -
                    (isFlat ? 0.5 : 0);

                setTuningPitch(noteValue);
            }
        } else {
            setTuningPitch(undefined);
        }
    }, [settings]);

    useInterval(getTuningNote, 25);

    const layoutNeumes = getLayoutNeumes(
        neumes,
        isSmallScreen,
        measureText,
        highlightedNote
    );

    const svgRows: JSX.Element[] = [];

    let currentSvgRow: JSX.Element[] = [];

    const pushRow = () => {
        const isDropCapRow = dropCap && svgRows.length === 0;

        const staveLineStart = isDropCapRow ? dropCapWidth + 4 : 0;

        svgRows.push(
            <svg
                width={measure.width}
                height={
                    dropCapText === "Q" && svgRows.length === 0
                        ? 90
                        : dropCapText === "J" && svgRows.length === 0
                        ? 84
                        : 78
                }
            >
                <g transform="translate(0, 16)">
                    <defs>
                        <g id="punctum-quadratum">
                            <path
                                d="M3 1C3.98202 1 4.6106 1.1275 4.985 1.3615C5.30525 1.56165 5.5 1.87871 5.5 2.5V6.54149C4.87081 6.22134 4.03265 6.00001 3 6.00001C1.96735 6.00001 1.12919 6.22134 0.5 6.54149V2.5C0.5 1.87871 0.694752 1.56165 1.015 1.3615C1.3894 1.1275 2.01798 1 3 1Z"
                                fill="black"
                                stroke="black"
                            />
                        </g>
                        <g id="podatus-upper">
                            <path
                                d="M2.5 0.5C3.48202 0.5 4.1106 0.627497 4.485 0.861499C4.80525 1.06165 5 1.37871 5 2V6.5H4.99151C4.98747 6.42532 4.9815 6.35214 4.9727 6.28173C4.94402 6.05226 4.87997 5.79829 4.7125 5.575C4.3662 5.11327 3.75425 5 3 5C1.96735 5 1.12919 5.22133 0.5 5.54148V2C0.5 1.27668 0.627668 0.981964 0.820092 0.821611C1.04519 0.634033 1.50249 0.5 2.5 0.5Z"
                                fill="black"
                                stroke="black"
                            />
                        </g>
                        <g id="podatus-lower">
                            <path
                                d="M5.38032 0.5H5.50025V0.640625L5.50025 2.125L5.50025 5C5.50025 5.2368 5.49964 5.38557 5.49081 5.49056C5.38582 5.49939 5.23705 5.5 5.00025 5.5H4.99437H4.98848H4.98256H4.97663H4.97066H4.96468H4.95867H4.95265H4.94659H4.94052H4.93443H4.92831H4.92217H4.91601H4.90983H4.90363H4.8974H4.89116H4.88489H4.8786H4.87229H4.86596H4.85961H4.85323H4.84684H4.84043H4.83399H4.82754H4.82106H4.81457H4.80805H4.80151H4.79496H4.78838H4.78179H4.77517H4.76854H4.76188H4.75521H4.74851H4.7418H4.73507H4.72832H4.72155H4.71476H4.70795H4.70112H4.69428H4.68741H4.68053H4.67363H4.66671H4.65977H4.65281H4.64584H4.63885H4.63184H4.62481H4.61777H4.6107H4.60362H4.59652H4.58941H4.58228H4.57513H4.56796H4.56078H4.55358H4.54636H4.53912H4.53187H4.52461H4.51732H4.51002H4.50271H4.49537H4.48802H4.48066H4.47328H4.46588H4.45847H4.45104H4.4436H4.43614H4.42867H4.42118H4.41367H4.40615H4.39862H4.39107H4.3835H4.37592H4.36833H4.36072H4.3531H4.34546H4.33781H4.33014H4.32246H4.31477H4.30706H4.29934H4.2916H4.28385H4.27609H4.26831H4.26052H4.25272H4.2449H4.23707H4.22923H4.22138H4.21351H4.20563H4.19773H4.18982H4.18191H4.17397H4.16603H4.15807H4.15011H4.14213H4.13413H4.12613H4.11811H4.11009H4.10205H4.09399H4.08593H4.07786H4.06977H4.06168H4.05357H4.04545H4.03732H4.02918H4.02103H4.01287H4.0047H3.99652H3.98832H3.98012H3.97191H3.96368H3.95545H3.94721H3.93896H3.93069H3.92242H3.91414H3.90585H3.89755H3.88924H3.88092H3.87259H3.86425H3.8559H3.84755H3.83918H3.83081H3.82243H3.81404H3.80564H3.79724H3.78882H3.7804H3.77197H3.76353H3.75508H3.74663H3.73816H3.72969H3.72122H3.71273H3.70424H3.69574H3.68723H3.67872H3.6702H3.66167H3.65314H3.6446H3.63605H3.6275H3.61893H3.61037H3.60179H3.59322H3.58463H3.57604H3.56744H3.55884H3.55023H3.54162H3.533H3.52437H3.51574H3.5071H3.49846H3.48982H3.48116H3.47251H3.46385H3.45518H3.44651H3.43784H3.42916H3.42047H3.41178H3.40309H3.39439H3.38569H3.37699H3.36828H3.35957H3.35085H3.34213H3.33341H3.32469H3.31596H3.30722H3.29849H3.28975H3.28101H3.27226H3.26351H3.25476H3.24601H3.23726H3.2285H3.21974H3.21098H3.20221H3.19345H3.18468H3.17591H3.16713H3.15836H3.14959H3.14081H3.13203H3.12325H3.11447H3.10569H3.0969H3.08812H3.07934H3.07055H3.06176H3.05298H3.04419H3.0354H3.02661H3.01782H3.00903H3.00024H2.99146H2.98267H2.97388H2.96509H2.9563H2.94751H2.93873H2.92994H2.92115H2.91237H2.90358H2.8948H2.88602H2.87724H2.86846H2.85968H2.8509H2.84213H2.83335H2.82458H2.81581H2.80704H2.79828H2.78951H2.78075H2.77199H2.76323H2.75448H2.74573H2.73698H2.72823H2.71948H2.71074H2.702H2.69327H2.68453H2.6758H2.66708H2.65836H2.64964H2.64092H2.63221H2.6235H2.6148H2.60609H2.5974H2.58871H2.58002H2.57133H2.56265H2.55398H2.54531H2.53664H2.52798H2.51933H2.51067H2.50203H2.49339H2.48475H2.47612H2.46749H2.45887H2.45026H2.44165H2.43305H2.42445H2.41586H2.40727H2.39869H2.39012H2.38155H2.37299H2.36444H2.35589H2.34735H2.33882H2.33029H2.32177H2.31326H2.30475H2.29625H2.28776H2.27927H2.2708H2.26233H2.25386H2.24541H2.23696H2.22852H2.22009H2.21167H2.20325H2.19485H2.18645H2.17806H2.16968H2.16131H2.15294H2.14459H2.13624H2.1279H2.11957H2.11125H2.10294H2.09464H2.08635H2.07807H2.0698H2.06153H2.05328H2.04504H2.0368H2.02858H2.02037H2.01217H2.00397H1.99579H1.98762H1.97946H1.97131H1.96317H1.95504H1.94692H1.93881H1.93072H1.92263H1.91456H1.90649H1.89844H1.8904H1.88238H1.87436H1.86636H1.85836H1.85038H1.84242H1.83446H1.82652H1.81858H1.81066H1.80276H1.79486H1.78698H1.77911H1.77126H1.76342H1.75559H1.74777H1.73997H1.73218H1.7244H1.71664H1.70889H1.70115H1.69343H1.68572H1.67803H1.67035H1.66268H1.65503H1.64739H1.63977H1.63216H1.62457H1.61699H1.60942H1.60187H1.59434H1.58682H1.57931H1.57182H1.56435H1.55689H1.54945H1.54202H1.53461H1.52721H1.51983H1.51247H1.50512H1.49778H1.49047H1.48317H1.47588H1.46862H1.46137H1.45413H1.44691H1.43971H1.43253H1.42536H1.41821H1.41108H1.40397H1.39687H1.38979H1.38272H1.37568H1.36865H1.36164H1.35465H1.34768H1.34072H1.33378H1.32686H1.31996H1.31308H1.30621H1.29937H1.29254H1.28573H1.27894H1.27217H1.26542H1.25869H1.25198H1.24528H1.23861H1.23195H1.22532H1.2187H1.21211H1.20553H1.19898H1.19244H1.18592H1.17943H1.17295H1.1665H1.16006H1.15365H1.14726H1.14088H1.13453H1.1282H1.12189H1.1156H1.10933H1.10309H1.09686H1.09066H1.08448H1.07832H1.07218H1.06606H1.05997H1.0539H1.04784H1.04182H1.03581H1.02983H1.02386H1.01793H1.01201H1.00612H1.00024C0.763439 5.5 0.614674 5.49939 0.509678 5.49057C0.500851 5.38557 0.500244 5.2368 0.500244 5V4.99413V4.98823V4.98232V4.97638V4.97042V4.96443V4.95843V4.9524V4.94635V4.94028V4.93419V4.92807V4.92193V4.91578V4.9096V4.9034V4.89717V4.89093V4.88467V4.87838V4.87208V4.86575V4.85941V4.85304V4.84665V4.84025V4.83382V4.82737V4.82091V4.81442V4.80791V4.80139V4.79485V4.78828V4.7817V4.7751V4.76848V4.76184V4.75518V4.7485V4.74181V4.7351V4.72837V4.72162V4.71485V4.70806V4.70126V4.69444V4.6876V4.68075V4.67387V4.66699V4.66008V4.65315V4.64621V4.63926V4.63228V4.62529V4.61828V4.61126V4.60422V4.59717V4.59009V4.58301V4.5759V4.56878V4.56165V4.5545V4.54734V4.54015V4.53296V4.52575V4.51852V4.51128V4.50403V4.49676V4.48948V4.48218V4.47487V4.46754V4.4602V4.45285V4.44548V4.4381V4.43071V4.4233V4.41588V4.40844V4.401V4.39354V4.38606V4.37858V4.37108V4.36357V4.35604V4.34851V4.34096V4.3334V4.32583V4.31825V4.31065V4.30304V4.29543V4.2878V4.28016V4.2725V4.26484V4.25717V4.24948V4.24179V4.23408V4.22636V4.21864V4.2109V4.20315V4.19539V4.18763V4.17985V4.17206V4.16426V4.15646V4.14864V4.14082V4.13299V4.12514V4.11729V4.10943V4.10156V4.09368V4.0858V4.0779V4.07V4.06209V4.05417V4.04624V4.03831V4.03036V4.02241V4.01446V4.00649V3.99852V3.99054V3.98255V3.97456V3.96656V3.95856V3.95054V3.94252V3.9345V3.92646V3.91843V3.91038V3.90233V3.89428V3.88621V3.87815V3.87007V3.862V3.85391V3.84582V3.83773V3.82963V3.82153V3.81342V3.80531V3.79719V3.78907V3.78095V3.77282V3.76468V3.75654V3.7484V3.74026V3.73211V3.72396V3.7158V3.70764V3.69948V3.69132V3.68315V3.67498V3.66681V3.65863V3.65046V3.64228V3.63409V3.62591V3.61772V3.60953V3.60134V3.59315V3.58496V3.57676V3.56857V3.56037V3.55217V3.54397V3.53577V3.52757V3.51937V3.51117V3.50297V3.49476V3.48656V3.47836V3.47015V3.46195V3.45375V3.44555V3.43734V3.42914V3.42094V3.41274V3.40454V3.39635V3.38815V3.37995V3.37176V3.36357V3.35538V3.34719V3.339V3.33081V3.32263V3.31445V3.30627V3.29809V3.28992V3.28175V3.27358V3.26541V3.25725V3.24909V3.24093V3.23278V3.22463V3.21648V3.20834V3.2002V3.19206V3.18393V3.1758V3.16768V3.15956V3.15144V3.14333V3.13523V3.12713V3.11903V3.11094V3.10285V3.09477V3.08669V3.07862V3.07056V3.0625V3.05445V3.0464V3.03836V3.03032V3.02229V3.01427V3.00625V2.99824V2.99023V2.98224V2.97425V2.96626V2.95829V2.95032V2.94236V2.9344V2.92645V2.91851V2.91058V2.90266V2.89474V2.88684V2.87894V2.87104V2.86316V2.85529V2.84742V2.83957V2.83172V2.82388V2.81605V2.80823V2.80041V2.79261V2.78482V2.77704V2.76926V2.7615V2.75375V2.746V2.73827V2.73055V2.72283V2.71513V2.70744V2.69976V2.69209V2.68443V2.67678V2.66915V2.66152V2.65391V2.64631V2.63872V2.63114V2.62357V2.61602V2.60847V2.60094V2.59342V2.58592V2.57842V2.57094V2.56348V2.55602V2.54858V2.54115V2.53373V2.52633V2.51894V2.51157V2.5042V2.49686V2.48952V2.4822V2.4749V2.4676V2.46033V2.45306V2.44582V2.43858V2.43136V2.42416V2.41697V2.40979V2.40264V2.39549V2.38836V2.38125V2.37415V2.36707V2.36001V2.35296V2.34593V2.33891V2.33191V2.32492V2.31796V2.31101V2.30407V2.29715V2.29025V2.28337V2.27651V2.26966V2.26283V2.25601V2.24922V2.24244V2.23568V2.22894V2.22221V2.21551V2.20882V2.20215V2.1955V2.18887V2.18226V2.17566V2.16909V2.16253V2.156V2.14948V2.14298V2.1365V2.13004V2.12361V2.11719V2.11079V2.10441V2.09805V2.09171V2.0854V2.0791V2.07282V2.06657V2.06033V2.05412V2.04793V2.04176V2.03561V2.02948V2.02337V2.01729V2.01122V2.00518V1.99916V1.99317V1.98719V1.98124V1.97531V1.9694V1.96352V1.95766V1.95182V1.946V1.94021V1.93444V1.9287V1.92297V1.91727V1.9116V1.90595V1.90032V1.89472V1.88914V1.88358V1.87805V1.87255V1.86707V1.86161V1.85618V1.85077V1.84539V1.84003V1.8347V1.8294V1.82412V1.81886V1.81363V1.80843V1.80325V1.7981V1.79297V1.78788V1.7828V1.77776V1.77274V1.76775V1.76278V1.75784V1.75293V1.74805V1.74319V1.73836V1.73356V1.72878V1.72403V1.71932V1.71462V1.70996V1.70533V1.70072V1.69614V1.69159V1.68707V1.68258V1.67812V1.67368V1.66928V1.6649V1.66055V1.65624V1.65195V1.64769V1.64346V1.63926V1.63509V1.63096V1.62685V1.62277V1.61872V1.6147V1.61072V1.60676V1.60284V1.59894V1.59508V1.59125V1.58745V1.58368V1.57994V1.57624V1.57256V1.56892V1.56531V1.56173V1.55819V1.55467V1.55119V1.54774V1.54433V1.54095V1.5376V1.53428V1.53099V1.52774V1.52453V1.52134V1.51819V1.51508V1.51199V1.50894V1.50593V1.50295V1.5C0.500244 1.35551 0.542504 1.29595 0.559582 1.27714C0.579937 1.25473 0.599893 1.25004 0.609294 1.24973C0.768646 1.24453 1.03718 1.24546 1.52935 1.24715C1.88695 1.24838 2.36262 1.25001 3.00025 1.25C3.7581 1.24998 4.34097 1.18969 4.74845 1.02669C4.96082 0.941734 5.1572 0.816705 5.29867 0.628096C5.32992 0.586436 5.35702 0.543635 5.38032 0.5Z"
                                fill="black"
                                stroke="black"
                            />
                        </g>
                        <g id="porrectus-1">
                            <path
                                d="M18 12.5C14.3216 12.5 10.3436 11.2814 8 10.5C4.61444 9.36039 2.40875 8.11637 0 6V0C1.79037 1.75817 3.83325 2.81862 8 5C9.92044 6.00946 13.44 7 16.5 7C17.8346 7 18.6744 6.23725 19 5V11.5C19 11.9557 18.7812 12.5 18 12.5Z"
                                fill="black"
                            />
                        </g>
                        <g id="porrectus-2">
                            <path
                                d="M18 17.5C14.3216 17.5 10 15 8 13.5C5.33333 11.5 2 8.5 0 6V0C2 2.5 5 5.5 8 8C9.66672 9.38893 14 12 16.5 12C17.8346 12 18.6744 11.2373 19 10V16.5C19 16.9557 18.7812 17.5 18 17.5Z"
                                fill="black"
                            />
                        </g>
                        <g id="porrectus-3">
                            <path
                                d="M18 23.5C14.3216 23.5 10 19.5 7 16C4.8307 13.4691 2 9.5 0 6V0C2 3.5 5.5 8.5 9 12.5C11.1082 14.9094 14 18 16.5 18C17.8346 18 18.6744 17.2373 19 16V22.5C19 22.9557 18.7812 23.5 18 23.5Z"
                                fill="black"
                            />
                        </g>
                        <g id="punctum-inclinatum">
                            <path
                                d="M3 7.0566L1.08962 4.00001L3 0.943399L4.91038 4.00001L3 7.0566Z"
                                fill="black"
                                stroke="black"
                            />
                        </g>
                        <g id="custos">
                            <path
                                d="M2.5 0.006505C2.85812 -0.0259952 3.00001 0.00647604 3.00001 1.50647V18.5063C2.00001 18.5063 2.5 18.5062 2 18.5063C0 18.5065 0 16.5065 0 14.5065V11.5063C0.358124 11.8318 0.741699 12.5063 1.5 12.5063C2 12.5063 1.54438 12.5063 2.00001 12.5063L2 2.50626C2.00001 1.50648 2 0.506504 2.5 0.006505Z"
                                fill="black"
                            />
                        </g>
                        <g id="beginning-ascending-liquescent">
                            <path
                                d="M0 6V1.00002C0.260437 1.45577 0.972895 2.00011 3 2.00011C5.5 2.00011 6 0.000108242 6 0.000108242V6C6 7 4.97656 7.20713 4 7.5C3.65964 7.60208 2 7.66667 1.5 7.5C0.666107 7.22204 0 6.84644 0 6Z"
                                fill="black"
                            />
                        </g>
                        <g id="terminating-ascending-liquescent">
                            <path
                                d="M2.5 5C2.5 4.25125 2.1185 4 1.5 4C0.8165 4 0.390625 4.04425 0 4.5V1.80656C0 1.38344 0.16275 1.00906 0.488312 0.683563C0.813812 0.357938 1.15562 0.146437 1.51369 0.0487499C1.67637 0.0162499 1.83912 0 2.00181 0C2.22975 0 2.44131 0.0487499 2.63663 0.146437C2.96225 0.309187 3 0.641875 3 1V7H2.539L2.5 5Z"
                                fill="black"
                            />
                        </g>
                        <g id="terminating-descending-liquescent">
                            <path
                                d="M2.5 2C2.5 2.74875 2.1185 3 1.5 3C0.8165 3 0.390625 2.95575 3.93402e-07 2.5L1.57935e-07 5.19344C1.20944e-07 5.61656 0.16275 5.99094 0.488313 6.31644C0.813813 6.64206 1.15563 6.85356 1.51369 6.95125C1.67638 6.98375 1.83913 7 2.00181 7C2.22975 7 2.44131 6.95125 2.63663 6.85356C2.96225 6.69081 3 6.35813 3 6L3 2.62268e-07L2.539 2.21966e-07L2.5 2Z"
                                fill="black"
                            />
                        </g>
                        <g id="mora">
                            <circle cx="1.5" cy="1.5" r="1.5" fill="black" />
                        </g>
                        <g id="quilisma">
                            <path
                                d="M0 6.62507V2.04171C0.3438 3.1528 0.7812 3.70837 1.3125 3.70837C1.78128 3.70837 2.0625 2.87504 2.15628 1.20837C2.37498 1.87504 2.57028 2.34032 2.74218 2.60421C2.91408 2.86811 3.15618 3.00005 3.46878 3.00005C3.78126 3.00005 4.03122 2.23611 4.21878 0.70832C4.56258 1.93056 4.8594 2.54165 5.10936 2.54165C5.45316 2.54165 5.74998 1.69445 6 0V5.33333C6 6.27771 5.8125 6.74987 5.4375 6.74987C5.15622 6.74987 4.8906 6.43051 4.6407 5.79157C4.5156 6.95824 4.15626 7.5416 3.5625 7.5416C3.09372 7.5416 2.59374 7.15269 2.0625 6.37493C1.78122 7.45824 1.42188 8 0.9843 8C0.67188 8 0.42966 7.86107 0.25782 7.58325C0.0859201 7.3056 0 6.98613 0 6.62507Z"
                                fill="black"
                            />
                        </g>
                        <g id="c-clef">
                            <path
                                d="M0 15.1014V3.85379C0 2.68873 0.3594 1.75522 1.0782 1.0531C1.79688 0.351033 2.73438 0 3.89064 0C4.54686 0 5.06244 0.179303 5.43744 0.537792C5.81244 0.896281 6 1.37425 6 1.97175V8.15572L5.625 7.84197C5.37498 7.6329 5.00778 7.41637 4.5234 7.19227C4.03908 6.96818 3.51564 6.85615 2.9532 6.85615C2.2656 6.85615 1.66404 7.11008 1.1484 7.61799C0.63282 8.12584 0.375 8.7382 0.375 9.45517C0.375 10.1722 0.63282 10.7921 1.1484 11.3149C1.66404 11.8377 2.2656 12.0991 2.9532 12.0991C3.51564 12.0991 4.03908 11.9946 4.5234 11.7855C5.00778 11.5763 5.37498 11.3672 5.625 11.1581L6 10.7996V17.0283C6 17.5959 5.8125 18.0665 5.4375 18.4399C5.0625 18.8133 4.54686 19 3.8907 19C2.73438 19 1.79688 18.6415 1.07814 17.9245C0.3594 17.2076 0 16.2666 0 15.1014V15.1014Z"
                                fill="black"
                            />
                        </g>
                        <g id="f-clef">
                            <path
                                d="M5.32231 8.40046V18.9063C5.32231 19.4683 5.19531 19.9688 5 20C4.90225 20 4.78525 19.6406 4.78525 19.3711C4.78525 19.1914 4.78513 18.9623 4.78525 18.8374V13.6931C4.26431 13.1936 3.54819 12.9437 2.63675 12.9437C1.52988 12.9437 0.651125 13.1779 0 13.6463V8.40046C0 7.30752 0.887062 6.7611 2.66112 6.7611C4.43519 6.7611 5.32231 7.30758 5.32231 8.40046ZM6.15244 15.8946V4.79393C6.15244 3.63864 6.51056 2.70181 7.22662 1.98363C7.94275 1.2654 8.88675 0.906281 10.0587 0.906281C10.7423 0.906281 11.2549 1.07805 11.5968 1.42152C11.9385 1.76505 12.1094 2.2334 12.1094 2.82663V9.00934L11.7676 8.72834C11.5398 8.50969 11.1817 8.2911 10.6934 8.07246C10.2051 7.85393 9.70056 7.74469 9.17969 7.74463C8.43106 7.74463 7.80444 7.99446 7.29987 8.49404C6.79531 8.99369 6.543 9.59478 6.543 10.2974C6.543 10.9999 6.79531 11.6166 7.29987 12.1475C7.80444 12.6783 8.43113 12.9436 9.17975 12.9436C9.70056 12.9436 10.2051 12.8344 10.6935 12.6158C11.1816 12.3973 11.5398 12.1943 11.7676 12.0069L12.1095 11.6791V17.8616C12.1095 18.4549 11.9304 18.9312 11.5724 19.2903C11.2143 19.6493 10.7098 19.8289 10.0586 19.8289C8.88675 19.8289 7.94281 19.462 7.22675 18.7281C6.5105 17.9943 6.15244 17.0499 6.15244 15.8946Z"
                                fill="black"
                            />
                        </g>
                        <g id="flat">
                            <path
                                d="M0.506063 0C0.76305 0 0.891604 0.272091 0.891604 0.816326L0.795188 10.1224C2.11245 11.3741 3.36549 11.9999 4.55425 11.9999C5.1325 11.9999 5.42169 11.7551 5.42169 11.2653C5.42169 10.9388 5.35741 10.653 5.22892 10.4082C5.10042 10.1632 4.79526 9.89794 4.31324 9.61222C3.83147 9.32644 3.51008 9.14285 3.34945 9.06119L1.71087 8.28582V6.4489C2.77107 6.4489 3.75103 6.70072 4.6506 7.20404C5.55018 7.70747 6 8.20401 6 8.69386C6 12.2313 5.98396 13.9999 5.95188 14C3.5743 14 1.66269 13.3605 0.216876 12.0816C0.024105 7.94561 -0.0401725 4.20414 0.024105 0.857129C0.024105 0.285727 0.184737 0 0.506063 0Z"
                                fill="black"
                            />
                        </g>
                        <g id="punctum-quadratum-white">
                            <path
                                d="M3 1C3.98202 1 4.6106 1.1275 4.985 1.3615C5.30525 1.56165 5.5 1.87871 5.5 2.5V6.54149C4.87081 6.22134 4.03265 6.00001 3 6.00001C1.96735 6.00001 1.12919 6.22134 0.5 6.54149V2.5C0.5 1.87871 0.694752 1.56165 1.015 1.3615C1.3894 1.1275 2.01798 1 3 1Z"
                                fill="white"
                                stroke="black"
                            />
                        </g>
                        <g id="podatus-upper-white">
                            <path
                                d="M2.5 0.5C3.48202 0.5 4.1106 0.627497 4.485 0.861499C4.80525 1.06165 5 1.37871 5 2V6.5H4.99151C4.98747 6.42532 4.9815 6.35214 4.9727 6.28173C4.94402 6.05226 4.87997 5.79829 4.7125 5.575C4.3662 5.11327 3.75425 5 3 5C1.96735 5 1.12919 5.22133 0.5 5.54148V2C0.5 1.27668 0.627668 0.981964 0.820092 0.821611C1.04519 0.634033 1.50249 0.5 2.5 0.5Z"
                                fill="white"
                                stroke="black"
                            />
                        </g>
                        <g id="podatus-lower-white">
                            <path
                                d="M5.38032 0.5H5.50025V0.640625L5.50025 2.125L5.50025 5C5.50025 5.2368 5.49964 5.38557 5.49081 5.49056C5.38582 5.49939 5.23705 5.5 5.00025 5.5H4.99437H4.98848H4.98256H4.97663H4.97066H4.96468H4.95867H4.95265H4.94659H4.94052H4.93443H4.92831H4.92217H4.91601H4.90983H4.90363H4.8974H4.89116H4.88489H4.8786H4.87229H4.86596H4.85961H4.85323H4.84684H4.84043H4.83399H4.82754H4.82106H4.81457H4.80805H4.80151H4.79496H4.78838H4.78179H4.77517H4.76854H4.76188H4.75521H4.74851H4.7418H4.73507H4.72832H4.72155H4.71476H4.70795H4.70112H4.69428H4.68741H4.68053H4.67363H4.66671H4.65977H4.65281H4.64584H4.63885H4.63184H4.62481H4.61777H4.6107H4.60362H4.59652H4.58941H4.58228H4.57513H4.56796H4.56078H4.55358H4.54636H4.53912H4.53187H4.52461H4.51732H4.51002H4.50271H4.49537H4.48802H4.48066H4.47328H4.46588H4.45847H4.45104H4.4436H4.43614H4.42867H4.42118H4.41367H4.40615H4.39862H4.39107H4.3835H4.37592H4.36833H4.36072H4.3531H4.34546H4.33781H4.33014H4.32246H4.31477H4.30706H4.29934H4.2916H4.28385H4.27609H4.26831H4.26052H4.25272H4.2449H4.23707H4.22923H4.22138H4.21351H4.20563H4.19773H4.18982H4.18191H4.17397H4.16603H4.15807H4.15011H4.14213H4.13413H4.12613H4.11811H4.11009H4.10205H4.09399H4.08593H4.07786H4.06977H4.06168H4.05357H4.04545H4.03732H4.02918H4.02103H4.01287H4.0047H3.99652H3.98832H3.98012H3.97191H3.96368H3.95545H3.94721H3.93896H3.93069H3.92242H3.91414H3.90585H3.89755H3.88924H3.88092H3.87259H3.86425H3.8559H3.84755H3.83918H3.83081H3.82243H3.81404H3.80564H3.79724H3.78882H3.7804H3.77197H3.76353H3.75508H3.74663H3.73816H3.72969H3.72122H3.71273H3.70424H3.69574H3.68723H3.67872H3.6702H3.66167H3.65314H3.6446H3.63605H3.6275H3.61893H3.61037H3.60179H3.59322H3.58463H3.57604H3.56744H3.55884H3.55023H3.54162H3.533H3.52437H3.51574H3.5071H3.49846H3.48982H3.48116H3.47251H3.46385H3.45518H3.44651H3.43784H3.42916H3.42047H3.41178H3.40309H3.39439H3.38569H3.37699H3.36828H3.35957H3.35085H3.34213H3.33341H3.32469H3.31596H3.30722H3.29849H3.28975H3.28101H3.27226H3.26351H3.25476H3.24601H3.23726H3.2285H3.21974H3.21098H3.20221H3.19345H3.18468H3.17591H3.16713H3.15836H3.14959H3.14081H3.13203H3.12325H3.11447H3.10569H3.0969H3.08812H3.07934H3.07055H3.06176H3.05298H3.04419H3.0354H3.02661H3.01782H3.00903H3.00024H2.99146H2.98267H2.97388H2.96509H2.9563H2.94751H2.93873H2.92994H2.92115H2.91237H2.90358H2.8948H2.88602H2.87724H2.86846H2.85968H2.8509H2.84213H2.83335H2.82458H2.81581H2.80704H2.79828H2.78951H2.78075H2.77199H2.76323H2.75448H2.74573H2.73698H2.72823H2.71948H2.71074H2.702H2.69327H2.68453H2.6758H2.66708H2.65836H2.64964H2.64092H2.63221H2.6235H2.6148H2.60609H2.5974H2.58871H2.58002H2.57133H2.56265H2.55398H2.54531H2.53664H2.52798H2.51933H2.51067H2.50203H2.49339H2.48475H2.47612H2.46749H2.45887H2.45026H2.44165H2.43305H2.42445H2.41586H2.40727H2.39869H2.39012H2.38155H2.37299H2.36444H2.35589H2.34735H2.33882H2.33029H2.32177H2.31326H2.30475H2.29625H2.28776H2.27927H2.2708H2.26233H2.25386H2.24541H2.23696H2.22852H2.22009H2.21167H2.20325H2.19485H2.18645H2.17806H2.16968H2.16131H2.15294H2.14459H2.13624H2.1279H2.11957H2.11125H2.10294H2.09464H2.08635H2.07807H2.0698H2.06153H2.05328H2.04504H2.0368H2.02858H2.02037H2.01217H2.00397H1.99579H1.98762H1.97946H1.97131H1.96317H1.95504H1.94692H1.93881H1.93072H1.92263H1.91456H1.90649H1.89844H1.8904H1.88238H1.87436H1.86636H1.85836H1.85038H1.84242H1.83446H1.82652H1.81858H1.81066H1.80276H1.79486H1.78698H1.77911H1.77126H1.76342H1.75559H1.74777H1.73997H1.73218H1.7244H1.71664H1.70889H1.70115H1.69343H1.68572H1.67803H1.67035H1.66268H1.65503H1.64739H1.63977H1.63216H1.62457H1.61699H1.60942H1.60187H1.59434H1.58682H1.57931H1.57182H1.56435H1.55689H1.54945H1.54202H1.53461H1.52721H1.51983H1.51247H1.50512H1.49778H1.49047H1.48317H1.47588H1.46862H1.46137H1.45413H1.44691H1.43971H1.43253H1.42536H1.41821H1.41108H1.40397H1.39687H1.38979H1.38272H1.37568H1.36865H1.36164H1.35465H1.34768H1.34072H1.33378H1.32686H1.31996H1.31308H1.30621H1.29937H1.29254H1.28573H1.27894H1.27217H1.26542H1.25869H1.25198H1.24528H1.23861H1.23195H1.22532H1.2187H1.21211H1.20553H1.19898H1.19244H1.18592H1.17943H1.17295H1.1665H1.16006H1.15365H1.14726H1.14088H1.13453H1.1282H1.12189H1.1156H1.10933H1.10309H1.09686H1.09066H1.08448H1.07832H1.07218H1.06606H1.05997H1.0539H1.04784H1.04182H1.03581H1.02983H1.02386H1.01793H1.01201H1.00612H1.00024C0.763439 5.5 0.614674 5.49939 0.509678 5.49057C0.500851 5.38557 0.500244 5.2368 0.500244 5V4.99413V4.98823V4.98232V4.97638V4.97042V4.96443V4.95843V4.9524V4.94635V4.94028V4.93419V4.92807V4.92193V4.91578V4.9096V4.9034V4.89717V4.89093V4.88467V4.87838V4.87208V4.86575V4.85941V4.85304V4.84665V4.84025V4.83382V4.82737V4.82091V4.81442V4.80791V4.80139V4.79485V4.78828V4.7817V4.7751V4.76848V4.76184V4.75518V4.7485V4.74181V4.7351V4.72837V4.72162V4.71485V4.70806V4.70126V4.69444V4.6876V4.68075V4.67387V4.66699V4.66008V4.65315V4.64621V4.63926V4.63228V4.62529V4.61828V4.61126V4.60422V4.59717V4.59009V4.58301V4.5759V4.56878V4.56165V4.5545V4.54734V4.54015V4.53296V4.52575V4.51852V4.51128V4.50403V4.49676V4.48948V4.48218V4.47487V4.46754V4.4602V4.45285V4.44548V4.4381V4.43071V4.4233V4.41588V4.40844V4.401V4.39354V4.38606V4.37858V4.37108V4.36357V4.35604V4.34851V4.34096V4.3334V4.32583V4.31825V4.31065V4.30304V4.29543V4.2878V4.28016V4.2725V4.26484V4.25717V4.24948V4.24179V4.23408V4.22636V4.21864V4.2109V4.20315V4.19539V4.18763V4.17985V4.17206V4.16426V4.15646V4.14864V4.14082V4.13299V4.12514V4.11729V4.10943V4.10156V4.09368V4.0858V4.0779V4.07V4.06209V4.05417V4.04624V4.03831V4.03036V4.02241V4.01446V4.00649V3.99852V3.99054V3.98255V3.97456V3.96656V3.95856V3.95054V3.94252V3.9345V3.92646V3.91843V3.91038V3.90233V3.89428V3.88621V3.87815V3.87007V3.862V3.85391V3.84582V3.83773V3.82963V3.82153V3.81342V3.80531V3.79719V3.78907V3.78095V3.77282V3.76468V3.75654V3.7484V3.74026V3.73211V3.72396V3.7158V3.70764V3.69948V3.69132V3.68315V3.67498V3.66681V3.65863V3.65046V3.64228V3.63409V3.62591V3.61772V3.60953V3.60134V3.59315V3.58496V3.57676V3.56857V3.56037V3.55217V3.54397V3.53577V3.52757V3.51937V3.51117V3.50297V3.49476V3.48656V3.47836V3.47015V3.46195V3.45375V3.44555V3.43734V3.42914V3.42094V3.41274V3.40454V3.39635V3.38815V3.37995V3.37176V3.36357V3.35538V3.34719V3.339V3.33081V3.32263V3.31445V3.30627V3.29809V3.28992V3.28175V3.27358V3.26541V3.25725V3.24909V3.24093V3.23278V3.22463V3.21648V3.20834V3.2002V3.19206V3.18393V3.1758V3.16768V3.15956V3.15144V3.14333V3.13523V3.12713V3.11903V3.11094V3.10285V3.09477V3.08669V3.07862V3.07056V3.0625V3.05445V3.0464V3.03836V3.03032V3.02229V3.01427V3.00625V2.99824V2.99023V2.98224V2.97425V2.96626V2.95829V2.95032V2.94236V2.9344V2.92645V2.91851V2.91058V2.90266V2.89474V2.88684V2.87894V2.87104V2.86316V2.85529V2.84742V2.83957V2.83172V2.82388V2.81605V2.80823V2.80041V2.79261V2.78482V2.77704V2.76926V2.7615V2.75375V2.746V2.73827V2.73055V2.72283V2.71513V2.70744V2.69976V2.69209V2.68443V2.67678V2.66915V2.66152V2.65391V2.64631V2.63872V2.63114V2.62357V2.61602V2.60847V2.60094V2.59342V2.58592V2.57842V2.57094V2.56348V2.55602V2.54858V2.54115V2.53373V2.52633V2.51894V2.51157V2.5042V2.49686V2.48952V2.4822V2.4749V2.4676V2.46033V2.45306V2.44582V2.43858V2.43136V2.42416V2.41697V2.40979V2.40264V2.39549V2.38836V2.38125V2.37415V2.36707V2.36001V2.35296V2.34593V2.33891V2.33191V2.32492V2.31796V2.31101V2.30407V2.29715V2.29025V2.28337V2.27651V2.26966V2.26283V2.25601V2.24922V2.24244V2.23568V2.22894V2.22221V2.21551V2.20882V2.20215V2.1955V2.18887V2.18226V2.17566V2.16909V2.16253V2.156V2.14948V2.14298V2.1365V2.13004V2.12361V2.11719V2.11079V2.10441V2.09805V2.09171V2.0854V2.0791V2.07282V2.06657V2.06033V2.05412V2.04793V2.04176V2.03561V2.02948V2.02337V2.01729V2.01122V2.00518V1.99916V1.99317V1.98719V1.98124V1.97531V1.9694V1.96352V1.95766V1.95182V1.946V1.94021V1.93444V1.9287V1.92297V1.91727V1.9116V1.90595V1.90032V1.89472V1.88914V1.88358V1.87805V1.87255V1.86707V1.86161V1.85618V1.85077V1.84539V1.84003V1.8347V1.8294V1.82412V1.81886V1.81363V1.80843V1.80325V1.7981V1.79297V1.78788V1.7828V1.77776V1.77274V1.76775V1.76278V1.75784V1.75293V1.74805V1.74319V1.73836V1.73356V1.72878V1.72403V1.71932V1.71462V1.70996V1.70533V1.70072V1.69614V1.69159V1.68707V1.68258V1.67812V1.67368V1.66928V1.6649V1.66055V1.65624V1.65195V1.64769V1.64346V1.63926V1.63509V1.63096V1.62685V1.62277V1.61872V1.6147V1.61072V1.60676V1.60284V1.59894V1.59508V1.59125V1.58745V1.58368V1.57994V1.57624V1.57256V1.56892V1.56531V1.56173V1.55819V1.55467V1.55119V1.54774V1.54433V1.54095V1.5376V1.53428V1.53099V1.52774V1.52453V1.52134V1.51819V1.51508V1.51199V1.50894V1.50593V1.50295V1.5C0.500244 1.35551 0.542504 1.29595 0.559582 1.27714C0.579937 1.25473 0.599893 1.25004 0.609294 1.24973C0.768646 1.24453 1.03718 1.24546 1.52935 1.24715C1.88695 1.24838 2.36262 1.25001 3.00025 1.25C3.7581 1.24998 4.34097 1.18969 4.74845 1.02669C4.96082 0.941734 5.1572 0.816705 5.29867 0.628096C5.32992 0.586436 5.35702 0.543635 5.38032 0.5Z"
                                fill="white"
                                stroke="black"
                            />
                        </g>
                        <g id="porrectus-1-left-white">
                            <path
                                d="M18 12.5C14.3216 12.5 10.3436 11.2814 8 10.5C4.61444 9.36039 2.40875 8.11637 0 6V0C1.79037 1.75817 3.83325 2.81862 8 5C9.92044 6.00946 13.44 7 16.5 7C17.8346 7 18.6744 6.23725 19 5V11.5C19 11.9557 18.7812 12.5 18 12.5Z"
                                fill="black"
                            />
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M8.3177 9.55181C10.6601 10.3328 14.5003 11.5 18 11.5V7.7044C17.5455 7.90223 17.0399 8 16.5 8C13.2757 8 9.59731 6.96911 7.53553 5.88559C7.44677 5.83912 7.3589 5.79313 7.27191 5.7476C4.44358 4.2672 2.54142 3.27158 1 2.14893V5.54262C3.16827 7.38531 5.20704 8.50461 8.3177 9.55181ZM8 10.5C4.61444 9.36039 2.40875 8.11637 0 6V0C0.321573 0.315789 0.651292 0.609069 1 0.890378C2.5717 2.15829 4.52916 3.18302 7.86525 4.92946L8 5C9.92044 6.00946 13.44 7 16.5 7C17.1009 7 17.6015 6.84538 18 6.56245C18.4866 6.21698 18.821 5.6802 19 5V11.5C19 11.9557 18.7812 12.5 18 12.5C14.3216 12.5 10.3436 11.2814 8 10.5Z"
                                fill="black"
                            />
                            <path
                                fill-rule="evenodd"
                                clip-rule="evenodd"
                                d="M8.3177 9.55181C10.6601 10.3328 14.5003 11.5 18 11.5V7.7044C17.5455 7.90223 17.0399 8 16.5 8C13.2757 8 9.59731 6.96911 7.53553 5.88559C7.44677 5.83912 7.3589 5.79313 7.27191 5.7476C4.44358 4.2672 2.54142 3.27158 1 2.14893V5.54262C3.16827 7.38531 5.20704 8.50461 8.3177 9.55181ZM8 10.5C4.61444 9.36039 2.40875 8.11637 0 6V0C0.321573 0.315789 0.651292 0.609069 1 0.890378C2.5717 2.15829 4.52916 3.18302 7.86525 4.92946L8 5C9.92044 6.00946 13.44 7 16.5 7C17.1009 7 17.6015 6.84538 18 6.56245C18.4866 6.21698 18.821 5.6802 19 5V11.5C19 11.9557 18.7812 12.5 18 12.5C14.3216 12.5 10.3436 11.2814 8 10.5Z"
                                fill="black"
                            />
                            <mask
                                id="mask0_0_1"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="0"
                                y="0"
                                width="6"
                                height="14"
                            >
                                <rect width="6" height="14" fill="#D9D9D9" />
                            </mask>
                            <g mask="url(#mask0_0_1)">
                                <path
                                    d="M18 12C14.411 12 10.502 10.8071 8.15895 10.0259C4.91009 8.93229 2.78803 7.75093 0.5 5.77255V1.12666C2.14683 2.50043 4.1607 3.55462 7.5652 5.33676C7.63218 5.37182 7.69969 5.40716 7.76776 5.44279C9.75887 6.48928 13.3579 7.5 16.5 7.5C17.2671 7.5 17.928 7.27866 18.4471 6.84924C18.4649 6.83447 18.4826 6.8195 18.5 6.80433V11.5C18.5 11.6591 18.4603 11.7854 18.4005 11.8611C18.3571 11.9161 18.2619 12 18 12Z"
                                    fill="white"
                                    stroke="black"
                                />
                                <path
                                    d="M18.5 6.94147L17.8005 7.24595C17.7448 7.27018 17.688 7.29272 17.6302 7.31355C17.8672 7.2281 18.0875 7.1135 18.2895 6.97015C18.3628 6.91809 18.433 6.86277 18.5 6.80438V6.94147ZM0.5 1.16621V1.12688C0.561476 1.17818 0.623478 1.22905 0.686061 1.27953C0.849664 1.41152 1.01725 1.54088 1.18978 1.66859L0.5 1.16621ZM7.50378 5.30461C5.33702 4.17049 3.73825 3.33287 2.4337 2.51275C3.75019 3.33961 5.3745 4.18994 7.57984 5.34441L7.63335 5.37243L7.50378 5.30461Z"
                                    fill="white"
                                    stroke="black"
                                />
                            </g>
                        </g>
                        <g id="porrectus-1-right-white">
                            <path
                                d="M18 12C14.411 12 10.502 10.8071 8.15895 10.0259C4.91009 8.93229 2.78803 7.75093 0.5 5.77255V1.12666C2.14683 2.50043 4.1607 3.55462 7.5652 5.33676C7.63218 5.37182 7.69969 5.40716 7.76776 5.44279C9.75887 6.48928 13.3579 7.5 16.5 7.5C17.2671 7.5 17.928 7.27866 18.4471 6.84924C18.4649 6.83447 18.4826 6.8195 18.5 6.80433V11.5C18.5 11.6591 18.4603 11.7854 18.4005 11.8611C18.3571 11.9161 18.2619 12 18 12Z"
                                fill="black"
                                stroke="black"
                            />
                            <mask
                                id="mask0_0_2"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="13"
                                y="4"
                                width="6"
                                height="13"
                            >
                                <rect
                                    x="13"
                                    y="4"
                                    width="6"
                                    height="13"
                                    fill="#D9D9D9"
                                />
                            </mask>
                            <g mask="url(#mask0_0_2)">
                                <path
                                    d="M18 12C14.411 12 10.502 10.8071 8.15895 10.0259C4.91009 8.93229 2.78803 7.75093 0.5 5.77255V1.12666C2.14683 2.50043 4.1607 3.55462 7.5652 5.33676C7.63218 5.37182 7.69969 5.40716 7.76776 5.44279C9.75887 6.48928 13.3579 7.5 16.5 7.5C17.2671 7.5 17.928 7.27866 18.4471 6.84924C18.4649 6.83447 18.4826 6.8195 18.5 6.80433V11.5C18.5 11.6591 18.4603 11.7854 18.4005 11.8611C18.3571 11.9161 18.2619 12 18 12Z"
                                    fill="white"
                                    stroke="black"
                                />
                            </g>
                        </g>
                        <g id="porrectus-2-left-white">
                            <path
                                d="M18 17C16.2627 17 14.3379 16.4063 12.5772 15.6072C10.8228 14.811 9.27352 13.8301 8.3 13.1C5.70245 11.1518 2.47638 8.25274 0.5 5.82345V1.38299C2.46722 3.66634 5.07527 6.21358 7.67991 8.38411C8.54654 9.1063 10.0785 10.1256 11.7085 10.9653C13.3186 11.7948 15.1202 12.5 16.5 12.5C17.2671 12.5 17.928 12.2787 18.4471 11.8492C18.4649 11.8345 18.4826 11.8195 18.5 11.8043V16.5C18.5 16.6591 18.4603 16.7854 18.4005 16.8611C18.3571 16.9161 18.2619 17 18 17Z"
                                fill="black"
                                stroke="black"
                            />
                            <mask
                                id="mask0_0_3"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="0"
                                y="0"
                                width="6"
                                height="13"
                            >
                                <rect width="6" height="13" fill="#D9D9D9" />
                            </mask>
                            <g mask="url(#mask0_0_3)">
                                <path
                                    d="M18 17C16.2627 17 14.3379 16.4063 12.5772 15.6072C10.8228 14.811 9.27352 13.8301 8.3 13.1C5.70245 11.1518 2.47638 8.25274 0.5 5.82345V1.38299C2.46722 3.66634 5.07527 6.21358 7.67991 8.38411C8.54654 9.1063 10.0785 10.1256 11.7085 10.9653C13.3186 11.7948 15.1202 12.5 16.5 12.5C17.2671 12.5 17.928 12.2787 18.4471 11.8492C18.4649 11.8345 18.4826 11.8195 18.5 11.8043V16.5C18.5 16.6591 18.4603 16.7854 18.4005 16.8611C18.3571 16.9161 18.2619 17 18 17Z"
                                    fill="white"
                                    stroke="black"
                                />
                            </g>
                        </g>
                        <g id="porrectus-2-right-white">
                            <path
                                d="M18 17C16.2627 17 14.3379 16.4063 12.5772 15.6072C10.8228 14.811 9.27352 13.8301 8.3 13.1C5.70245 11.1518 2.47638 8.25274 0.5 5.82345V1.38299C2.46722 3.66634 5.07527 6.21358 7.67991 8.38411C8.54654 9.1063 10.0785 10.1256 11.7085 10.9653C13.3186 11.7948 15.1202 12.5 16.5 12.5C17.2671 12.5 17.928 12.2787 18.4471 11.8492C18.4649 11.8345 18.4826 11.8195 18.5 11.8043V16.5C18.5 16.6591 18.4603 16.7854 18.4005 16.8611C18.3571 16.9161 18.2619 17 18 17Z"
                                fill="black"
                                stroke="black"
                            />
                            <mask
                                id="mask0_0_4"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="13"
                                y="7"
                                width="6"
                                height="17"
                            >
                                <rect
                                    x="13"
                                    y="7"
                                    width="6"
                                    height="17"
                                    fill="#D9D9D9"
                                />
                            </mask>
                            <g mask="url(#mask0_0_4)">
                                <path
                                    d="M18 17C16.2627 17 14.3379 16.4063 12.5772 15.6072C10.8228 14.811 9.27352 13.8301 8.3 13.1C5.70245 11.1518 2.47638 8.25274 0.5 5.82345V1.38299C2.46722 3.66634 5.07527 6.21358 7.67991 8.38411C8.54654 9.1063 10.0785 10.1256 11.7085 10.9653C13.3186 11.7948 15.1202 12.5 16.5 12.5C17.2671 12.5 17.928 12.2787 18.4471 11.8492C18.4649 11.8345 18.4826 11.8195 18.5 11.8043V16.5C18.5 16.6591 18.4603 16.7854 18.4005 16.8611C18.3571 16.9161 18.2619 17 18 17Z"
                                    fill="white"
                                    stroke="black"
                                />
                            </g>
                        </g>
                        <g id="porrectus-3-left-white">
                            <path
                                d="M18 23C16.3264 23 14.4355 22.081 12.5457 20.6626C10.6705 19.2551 8.86675 17.4096 7.37963 15.6746C5.25484 13.1957 2.48054 9.31157 0.5 5.86686V1.8162C2.57102 5.1694 5.59668 9.36979 8.62371 12.8293C9.67879 14.0351 10.9517 15.4371 12.2845 16.5389C13.5985 17.6251 15.0641 18.5 16.5 18.5C17.2671 18.5 17.928 18.2787 18.4471 17.8492C18.4649 17.8345 18.4826 17.8195 18.5 17.8043V22.5C18.5 22.6591 18.4603 22.7854 18.4005 22.8611C18.3571 22.9161 18.2619 23 18 23Z"
                                fill="black"
                                stroke="black"
                            />
                            <mask
                                id="mask0_0_5"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="0"
                                y="0"
                                width="6"
                                height="15"
                            >
                                <rect width="6" height="15" fill="#D9D9D9" />
                            </mask>
                            <g mask="url(#mask0_0_5)">
                                <path
                                    d="M18 23C16.3264 23 14.4355 22.081 12.5457 20.6626C10.6705 19.2551 8.86675 17.4096 7.37963 15.6746C5.25484 13.1957 2.48054 9.31157 0.5 5.86686V1.8162C2.57102 5.1694 5.59668 9.36979 8.62371 12.8293C9.67879 14.0351 10.9517 15.4371 12.2845 16.5389C13.5985 17.6251 15.0641 18.5 16.5 18.5C17.2671 18.5 17.928 18.2787 18.4471 17.8492C18.4649 17.8345 18.4826 17.8195 18.5 17.8043V22.5C18.5 22.6591 18.4603 22.7854 18.4005 22.8611C18.3571 22.9161 18.2619 23 18 23Z"
                                    fill="white"
                                    stroke="black"
                                />
                            </g>
                        </g>
                        <g id="porrectus-3-right-white">
                            <path
                                d="M18 23C16.3264 23 14.4355 22.081 12.5457 20.6626C10.6705 19.2551 8.86675 17.4096 7.37963 15.6746C5.25484 13.1957 2.48054 9.31157 0.5 5.86686V1.8162C2.57102 5.1694 5.59668 9.36979 8.62371 12.8293C9.67879 14.0351 10.9517 15.4371 12.2845 16.5389C13.5985 17.6251 15.0641 18.5 16.5 18.5C17.2671 18.5 17.928 18.2787 18.4471 17.8492C18.4649 17.8345 18.4826 17.8195 18.5 17.8043V22.5C18.5 22.6591 18.4603 22.7854 18.4005 22.8611C18.3571 22.9161 18.2619 23 18 23Z"
                                fill="black"
                                stroke="black"
                            />
                            <mask
                                id="mask0_0_6"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x="13"
                                y="10"
                                width="6"
                                height="17"
                            >
                                <rect
                                    x="13"
                                    y="10"
                                    width="6"
                                    height="17"
                                    fill="#D9D9D9"
                                />
                            </mask>
                            <g mask="url(#mask0_0_6)">
                                <path
                                    d="M18 23C16.3264 23 14.4355 22.081 12.5457 20.6626C10.6705 19.2551 8.86675 17.4096 7.37963 15.6746C5.25484 13.1957 2.48054 9.31157 0.5 5.86686V1.8162C2.57102 5.1694 5.59668 9.36979 8.62371 12.8293C9.67879 14.0351 10.9517 15.4371 12.2845 16.5389C13.5985 17.6251 15.0641 18.5 16.5 18.5C17.2671 18.5 17.928 18.2787 18.4471 17.8492C18.4649 17.8345 18.4826 17.8195 18.5 17.8043V22.5C18.5 22.6591 18.4603 22.7854 18.4005 22.8611C18.3571 22.9161 18.2619 23 18 23Z"
                                    fill="white"
                                    stroke="black"
                                />
                            </g>
                        </g>
                        <g id="punctum-inclinatum-white">
                            <path
                                d="M3 7.0566L1.08962 4.00001L3 0.943399L4.91038 4.00001L3 7.0566Z"
                                fill="white"
                                stroke="black"
                            />
                        </g>
                        <g id="beginning-ascending-liquescent-white">
                            <path
                                d="M0 6V1.00002C0.260437 1.45577 0.972895 2.00011 3 2.00011C5.5 2.00011 6 0.000108242 6 0.000108242V6C6 7 4.97656 7.20713 4 7.5C3.65964 7.60208 2 7.66667 1.5 7.5C0.666107 7.22204 0 6.84644 0 6Z"
                                fill="white"
                                stroke="black"
                            />
                        </g>
                        <g id="terminating-ascending-liquescent-white">
                            <path
                                d="M2.5 5C2.5 4.25125 2.1185 4 1.5 4C0.8165 4 0.390625 4.04425 0 4.5V1.80656C0 1.38344 0.16275 1.00906 0.488312 0.683563C0.813812 0.357938 1.15562 0.146437 1.51369 0.0487499C1.67637 0.0162499 1.83912 0 2.00181 0C2.22975 0 2.44131 0.0487499 2.63663 0.146437C2.96225 0.309187 3 0.641875 3 1V7H2.539L2.5 5Z"
                                fill="white"
                                stroke="black"
                            />
                        </g>
                        <g id="terminating-descending-liquescent-white">
                            <path
                                d="M2.5 2C2.5 2.74875 2.1185 3 1.5 3C0.8165 3 0.390625 2.95575 3.93402e-07 2.5L1.57935e-07 5.19344C1.20944e-07 5.61656 0.16275 5.99094 0.488313 6.31644C0.813813 6.64206 1.15563 6.85356 1.51369 6.95125C1.67638 6.98375 1.83913 7 2.00181 7C2.22975 7 2.44131 6.95125 2.63663 6.85356C2.96225 6.69081 3 6.35813 3 6L3 2.62268e-07L2.539 2.21966e-07L2.5 2Z"
                                fill="white"
                                stroke="black"
                            />
                        </g>
                        <g id="quilisma-white">
                            <path
                                d="M0.5 6.62507V3.91319C0.728921 4.09502 0.999827 4.20837 1.3125 4.20837C1.59911 4.20837 1.83223 4.07364 2.00056 3.88661C2.15839 3.71125 2.26771 3.48341 2.34876 3.24326C2.37363 3.16957 2.39696 3.09127 2.41879 3.00845C2.67886 3.32847 3.04075 3.50005 3.46878 3.50005C3.77518 3.50005 3.98003 3.30813 4.09406 3.15326C4.21032 2.99536 4.29659 2.79418 4.36583 2.58261C4.37034 2.56881 4.37483 2.55482 4.3793 2.54064C4.42882 2.62097 4.48229 2.69544 4.54049 2.76048C4.64485 2.87712 4.83542 3.04165 5.10936 3.04165C5.2635 3.04165 5.39334 2.99414 5.5 2.92666V5.33333C5.5 5.75923 5.4585 6.02984 5.40527 6.18269C5.32283 6.08069 5.21974 5.89936 5.10635 5.60945L4.14355 5.73826C4.08405 6.29315 3.97375 6.64363 3.85165 6.84183C3.74643 7.01261 3.65646 7.0416 3.5625 7.0416C3.47269 7.0416 3.33574 7.00524 3.1429 6.85525C2.94855 6.70409 2.72455 6.45773 2.47538 6.09292L1.85498 5.18463L1.57855 6.24928C1.4433 6.77016 1.30004 7.11429 1.16537 7.31729C1.0996 7.41642 1.04751 7.46399 1.01673 7.48492C1.00234 7.49469 0.993697 7.49795 0.990615 7.49895C0.987928 7.49982 0.986446 7.5 0.9843 7.5C0.854447 7.5 0.768791 7.45885 0.683048 7.32023L0.682939 7.32006C0.565096 7.12971 0.5 6.90335 0.5 6.62507ZM5.48238 6.2585C5.48238 6.2586 5.48126 6.25813 5.47904 6.25674C5.48127 6.25769 5.48239 6.25839 5.48238 6.2585Z"
                                fill="white"
                                stroke="black"
                            />
                        </g>
                    </defs>
                    {isDropCapRow ? (
                        <>
                            <text
                                x={
                                    dropCapWidth / 2 +
                                    (dropCapText === "J" ? 4 : 0)
                                }
                                y={4}
                                textAnchor="middle"
                                fontSize={14}
                                fontWeight={600}
                            >
                                {toneLabel}
                            </text>
                            <text
                                x={
                                    dropCapWidth / 2 +
                                    (dropCapText === "J" ? 4 : 0)
                                }
                                y={DROP_CAP_OFFSET}
                                textAnchor="middle"
                                style={{
                                    fontSize: `${DROP_CAP_FONT_SIZE}px`,
                                    fontFamily: BODY_FONT,
                                    fill: RUBRIC_COLOR,
                                }}
                            >
                                {dropCapText}
                            </text>
                        </>
                    ) : (
                        <></>
                    )}
                    {[0, 1, 2, 3].map((staveLine) => {
                        return (
                            <line
                                x1={staveLineStart}
                                y1={staveLine * STAVE_LINE_SPACING}
                                x2={measure.width}
                                y2={staveLine * STAVE_LINE_SPACING}
                                stroke={RUBRIC_COLOR}
                                strokeWidth={1}
                            />
                        );
                    })}
                    {tuningPitch && isOnScreen ? (
                        <line
                            x1={staveLineStart}
                            x2={measure.width}
                            y1={getPitchY(tuningPitch)}
                            y2={getPitchY(tuningPitch)}
                            strokeWidth={2}
                            stroke="rgba(0, 214, 255, 0.5)"
                        />
                    ) : (
                        <></>
                    )}
                    {clef === "c4" ? (
                        <use
                            xlinkHref="#c-clef"
                            x={isDropCapRow ? dropCapWidth + 4 : 0}
                            y={-9.5}
                        ></use>
                    ) : (
                        <></>
                    )}
                    {clef === "c3" ? (
                        <use
                            xlinkHref="#c-clef"
                            x={isDropCapRow ? dropCapWidth + 4 : 0}
                            y={1.5}
                        ></use>
                    ) : (
                        <></>
                    )}
                    {clef === "c2" ? (
                        <use
                            xlinkHref="#c-clef"
                            x={isDropCapRow ? dropCapWidth + 4 : 0}
                            y={12.5}
                        ></use>
                    ) : (
                        <></>
                    )}
                    {clef === "f3" ? (
                        <use
                            xlinkHref="#f-clef"
                            x={isDropCapRow ? dropCapWidth + 4 : 0}
                            y={0.5}
                        ></use>
                    ) : (
                        <></>
                    )}
                    {currentSvgRow}
                </g>
            </svg>
        );
        currentSvgRow = [];
    };

    const dropCapWidth = Math.max(
        measureText(toneLabel, "normal"),
        measureText(dropCapText, "drop cap")
    );

    if (hasEverBeenOnScreen) {
        let left: number | undefined = undefined;

        layoutNeumes.forEach((layoutNeume, layoutNeumeIndex) => {
            if (left === undefined) {
                left = clef === "f3" ? 20 : 12;

                left += Math.max(8 - layoutNeume.textExtensionLeft, 0);

                if (dropCap && svgRows.length === 0) {
                    left += dropCapWidth;
                }

                left += Math.max(
                    layoutNeume.neumeExtensionLeft,
                    layoutNeume.textExtensionLeft
                );
            }

            const nextLayoutNeume = layoutNeumes[layoutNeumeIndex + 1];
            const nextButOneLayoutNeume = layoutNeumes[layoutNeumeIndex + 2];

            let nextLayoutNeumeWithANote: LayoutNeume | undefined = undefined;
            for (
                let nlnwan = layoutNeumeIndex + 1;
                nlnwan < layoutNeumes.length;
                nlnwan++
            ) {
                if (layoutNeumes[nlnwan].neume.elements[0]?.notes.length > 0) {
                    nextLayoutNeumeWithANote = layoutNeumes[nlnwan];
                    break;
                }
            }

            let nextLeft = nextLayoutNeume
                ? left +
                  Math.max(
                      layoutNeume.neumeExtensionRight +
                          nextLayoutNeume.neumeExtensionLeft,
                      layoutNeume.textExtensionRight +
                          nextLayoutNeume.textExtensionLeft
                  )
                : 0;

            let nextNeumeNeedsNewLine = false;

            if (nextLayoutNeume) {
                let bufferSize = 4;

                if (
                    nextLeft +
                        Math.max(
                            nextLayoutNeume.neumeExtensionRight,
                            nextLayoutNeume.textExtensionRight
                        ) +
                        bufferSize >
                    measure.width
                ) {
                    // Next neume crosses threshold
                    nextNeumeNeedsNewLine = true;
                } else {
                    // Even if this neume does not cross the threshold, maybe the next one is a bar and does
                    if (
                        nextButOneLayoutNeume &&
                        nextButOneLayoutNeume.neume.elements[0] &&
                        nextButOneLayoutNeume.neume.elements[0].type.includes(
                            "bar"
                        ) &&
                        nextLeft +
                            Math.max(
                                nextLayoutNeume.neumeExtensionRight,
                                nextLayoutNeume.textExtensionRight
                            ) +
                            nextButOneLayoutNeume.neumeExtensionRight +
                            bufferSize >
                            measure.width
                    ) {
                        nextNeumeNeedsNewLine = true;
                    }
                }
            }

            let text = layoutNeume.neume.text
                .replace("[", "(")
                .replace("]", ")")
                .replace("$", "*");

            if (
                nextLayoutNeume &&
                layoutNeume.textExtensionRight +
                    nextLayoutNeume.textExtensionLeft >=
                    layoutNeume.neumeExtensionRight +
                        nextLayoutNeume.neumeExtensionLeft &&
                !nextNeumeNeedsNewLine &&
                text.slice(text.length - 1) !== " " &&
                layoutNeume.redDotOffset === undefined
            ) {
                // Accumulate text that touches and show it on the last neume

                let kernAdjustment =
                    measureText(text, "normal") +
                    measureText(nextLayoutNeume.neume.text, "normal") -
                    measureText(text + nextLayoutNeume.neume.text, "normal");

                // console.log("combining", text + nextLayoutNeume.neume.text, {
                //     addAfterMeasure:
                //         measureText(text, "normal") +
                //         measureText(nextLayoutNeume.neume.text, "normal"),
                //     addBeforemeasure: measureText(
                //         text + nextLayoutNeume.neume.text,
                //         "normal"
                //     ),
                //     kernAdjustment,
                // });

                nextLayoutNeume.textExtensionLeft +=
                    layoutNeume.textExtensionRight +
                    layoutNeume.textExtensionLeft -
                    kernAdjustment;
                nextLayoutNeume.neume.text = text + nextLayoutNeume.neume.text;

                nextLeft -= kernAdjustment;

                text = "";
            }

            const totalExtensionLeft = Math.max(
                layoutNeume.neumeExtensionLeft,
                layoutNeume.textExtensionLeft
            );

            const totalExtensionRight = Math.max(
                layoutNeume.neumeExtensionRight,
                layoutNeume.textExtensionRight
            );

            let currentSvgItem: JSX.Element[] = [];

            if (layoutNeume.maxNote >= 9) {
                currentSvgItem.push(
                    <line
                        x1={-layoutNeume.neumeExtensionLeft - 4}
                        y1={-1 * STAVE_LINE_SPACING}
                        x2={layoutNeume.neumeExtensionRight - 6}
                        y2={-1 * STAVE_LINE_SPACING}
                        stroke={RUBRIC_COLOR}
                        strokeWidth={1}
                    />
                );
            }

            if (layoutNeume.minNote <= -1) {
                currentSvgItem.push(
                    <line
                        x1={-layoutNeume.neumeExtensionLeft - 4}
                        y1={4 * STAVE_LINE_SPACING}
                        x2={layoutNeume.neumeExtensionRight - 6}
                        y2={4 * STAVE_LINE_SPACING}
                        stroke={RUBRIC_COLOR}
                        strokeWidth={1}
                    />
                );
            }

            currentSvgItem.push(...layoutNeume.svgNeume);

            currentSvgItem.push(
                <text
                    x={
                        -layoutNeume.textExtensionLeft +
                        (text === "* " ? -1 : 0)
                    }
                    y={TEXT_OFFSET}
                    style={{
                        fontSize: bodyFontSize,
                        fontFamily: BODY_FONT,
                        fontWeight:
                            layoutNeume.neume.isBold || text === "* "
                                ? 700
                                : undefined,
                        fill: text === "* " ? RUBRIC_COLOR : undefined,
                    }}
                    data-width={measureText(
                        layoutNeume.neume.text,
                        layoutNeume.neume.isBold ? "normal bold" : "normal"
                    )}
                    data-tl={layoutNeume.textExtensionLeft}
                    data-tr={layoutNeume.textExtensionRight}
                    data-nl={layoutNeume.neumeExtensionLeft}
                    data-nr={layoutNeume.neumeExtensionRight}
                    data-nnl={nextLayoutNeume?.neumeExtensionLeft}
                    data-ntl={nextLayoutNeume?.textExtensionLeft}
                    data-text-space={
                        layoutNeume.textExtensionRight +
                        nextLayoutNeume?.textExtensionLeft
                    }
                    data-neume-space={
                        layoutNeume.neumeExtensionRight +
                        nextLayoutNeume?.neumeExtensionLeft
                    }
                    data-concat={
                        nextLayoutNeume &&
                        layoutNeume.textExtensionRight +
                            nextLayoutNeume.textExtensionLeft >=
                            layoutNeume.neumeExtensionRight +
                                nextLayoutNeume.neumeExtensionLeft &&
                        !nextNeumeNeedsNewLine
                    }
                >
                    {text}
                    {nextNeumeNeedsNewLine &&
                    layoutNeume.neume.text.length > 0 &&
                    "abcdefghijklmnopqrstuvwxyz".includes(
                        layoutNeume.neume.text[
                            layoutNeume.neume.text.length - 1
                        ].toLocaleLowerCase()
                    )
                        ? "-"
                        : ""}
                </text>
            );

            currentSvgItem.push(
                <rect
                    width={totalExtensionLeft + totalExtensionRight}
                    height={100}
                    x={-totalExtensionLeft}
                    y={-20}
                    fill="transparent"
                />
            );

            if (layoutNeume.redDotOffset !== undefined) {
                currentSvgItem.push(
                    <text
                        x={layoutNeume.redDotOffset + 1}
                        y={TEXT_OFFSET + 0.5}
                        style={{
                            fontSize: bodyFontSize * 0.8,
                            fontFamily: BODY_FONT,
                            fill: DOT_COLOR,
                        }}
                    >
                        {"•"}
                    </text>
                );
            }

            currentSvgRow.push(
                <g
                    transform={`translate(${left}, 0)`}
                    onClick={() => {
                        if (
                            mistakeNeume === -1 ||
                            mistakeNeume === layoutNeumeIndex
                        ) {
                            if (mistakeClicks === 2) {
                                let verseIndex = 0;

                                for (let i = 0; i < layoutNeumeIndex; i++) {
                                    const ln = layoutNeumes[i];
                                    if (
                                        ln.neume.text.trim().length === 0 &&
                                        i !== 0
                                    ) {
                                        verseIndex++;
                                    }
                                }

                                console.log(
                                    "found mistake in verse",
                                    verseIndex
                                );

                                if (props.onReportMistake) {
                                    props.onReportMistake(verseIndex);
                                }
                            }

                            setMistakeClicks((p) => p + 1);
                            setTimeout(() => {
                                setMistakeClicks((p) => (p > 0 ? p - 1 : 0));
                            }, 1000);
                        } else {
                            setMistakeNeume(-1);
                            setMistakeClicks(0);
                        }

                        if (onClickNeume) {
                            onClickNeume(layoutNeumeIndex);
                        }
                    }}
                >
                    {currentSvgItem}
                </g>
            );

            if (nextLayoutNeume) {
                if (nextNeumeNeedsNewLine) {
                    left = undefined;

                    if (nextLayoutNeumeWithANote) {
                        currentSvgRow.push(
                            <use
                                xlinkHref="#custos"
                                x={measure.width - 3}
                                y={
                                    getPitchY(
                                        nextLayoutNeumeWithANote.neume
                                            .elements[0]?.notes[0]?.pitch ?? 0
                                    ) -
                                    4 -
                                    STAVE_SPACING
                                }
                            ></use>
                        );
                    }

                    pushRow();
                } else {
                    left = nextLeft;
                }
            }
        });

        pushRow();
    }

    return (
        <div ref={measureRef}>
            <div ref={containerRef} className="svg-chant">
                {svgRows}
            </div>
        </div>
    );
};

const useOnScreen = (ref: React.RefObject<HTMLDivElement>) => {
    // State and setter for storing whether element is visible
    const [isIntersecting, setIntersecting] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                // Update our state when observer callback fires
                setIntersecting(entry.isIntersecting);
            },
            {
                rootMargin: "0px",
            }
        );

        if (ref.current) {
            observer.observe(ref.current);
        }

        return () => {
            if (ref.current) {
                observer.unobserve(ref.current);
            }
        };
    }, []); // Empty array ensures that effect is only run on mount and unmount

    return isIntersecting;
};
